import { Injectable } from '@angular/core';

/**
 * Abstraction for text sanitization operations.
 * Following Dependency Inversion Principle - high-level modules depend on this interface.
 */
export interface ITextSanitizer {
    /**
     * Sanitizes HTML content allowing only safe highlight tags.
     * @param html The HTML string to sanitize
     * @returns Sanitized HTML string with only allowed tags
     */
    sanitizeHighlightHtml(html: string): string;
}

/**
 * Service responsible for sanitizing text content for safe HTML rendering.
 * Single Responsibility: Only handles text sanitization logic.
 *
 * This service provides secure sanitization that only allows specific HTML tags
 * (like <mark>) while escaping all other potentially dangerous content.
 * This is SSR-compatible as it doesn't rely on browser-specific APIs.
 */
@Injectable({
    providedIn: 'root',
})
export class TextSanitizerService implements ITextSanitizer {
    /**
     * List of allowed HTML tags for highlight rendering.
     * Only these tags will be preserved; all others will be escaped.
     */
    private readonly allowedTags = ['mark'];

    /**
     * Sanitizes HTML content, preserving only allowed highlight tags.
     * All other HTML is escaped to prevent XSS attacks.
     *
     * @param html The HTML string containing highlight markup
     * @returns Sanitized HTML safe for rendering
     */
    sanitizeHighlightHtml(html: string): string {
        if (!html) {
            return '';
        }

        // First, escape all HTML entities
        let sanitized = this.escapeHtml(html);

        // Then, restore only the allowed tags
        for (const tag of this.allowedTags) {
            // Restore opening tags: &lt;mark&gt; -> <mark>
            const openingTagPattern = new RegExp(`&lt;${tag}&gt;`, 'gi');
            sanitized = sanitized.replace(openingTagPattern, `<${tag}>`);

            // Restore closing tags: &lt;/mark&gt; -> </mark>
            const closingTagPattern = new RegExp(`&lt;/${tag}&gt;`, 'gi');
            sanitized = sanitized.replace(closingTagPattern, `</${tag}>`);
        }

        return sanitized;
    }

    /**
     * Escapes HTML special characters to prevent XSS.
     * @param text The text to escape
     * @returns Escaped text safe for HTML rendering
     */
    private escapeHtml(text: string): string {
        const htmlEscapes: Record<string, string> = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;',
        };

        return text.replace(/[&<>"']/g, (char) => htmlEscapes[char]);
    }
}
